import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Students API
export const studentsApi = {
  getAll: (params) => api.get('/students', { params }),
  getById: (id) => api.get(`/students/${id}`),
  create: (data) => api.post('/students', data),
  update: (id, data) => api.put(`/students/${id}`, data),
  delete: (id) => api.delete(`/students/${id}`),
};

// Teachers API
export const teachersApi = {
  getAll: (params) => api.get('/teachers', { params }),
  getById: (id) => api.get(`/teachers/${id}`),
  create: (data) => api.post('/teachers', data),
  update: (id, data) => api.put(`/teachers/${id}`, data),
  delete: (id) => api.delete(`/teachers/${id}`),
};

// Classes API
export const classesApi = {
  getAll: () => api.get('/classes'),
  getById: (id) => api.get(`/classes/${id}`),
  create: (data) => api.post('/classes', data),
  update: (id, data) => api.put(`/classes/${id}`, data),
  delete: (id) => api.delete(`/classes/${id}`),
  assignTeacher: (classId, teacherId) => {
    const formData = new FormData();
    formData.append('teacher_id', teacherId);
    // Explicit multipart/form-data so axios auto-generates the boundary.
    // The instance default is application/json which, on production builds,
    // causes the browser to omit the multipart boundary and the FastAPI
    // Form(...) parser sees no fields → assignment silently fails.
    return api.post(`/classes/${classId}/assign-teacher`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Subjects API
export const subjectsApi = {
  getAll: (classId) => api.get('/subjects', { params: { class_id: classId } }),
  create: (data) => api.post('/subjects', data),
  update: (id, data) => api.put(`/subjects/${id}`, data),
  delete: (id) => api.delete(`/subjects/${id}`),
};

// Attendance API
export const attendanceApi = {
  getAll: (params) => api.get('/attendance', { params }),
  create: (data) => api.post('/attendance', data),
  createBulk: (data) => api.post('/attendance/bulk', data),
  getStats: (studentId) => api.get(`/attendance/stats/${studentId}`),
  export: (classId, startDate, endDate) => 
    api.get('/attendance/export', { 
      params: { class_id: classId, start_date: startDate, end_date: endDate },
      responseType: 'blob'
    }),
};

// Marks API
export const marksApi = {
  getAll: (params) => api.get('/marks', { params }),
  create: (data) => api.post('/marks', data),
  createBulk: (data) => api.post('/marks/bulk', data),
  getStats: (studentId) => api.get(`/marks/stats/${studentId}`),
};

// Marksheets API
export const marksheetsApi = {
  getAll: (params) => api.get('/marksheets', { params }),
  upload: (formData) => api.post('/marksheets/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  download: (filename) => `${API_URL}/api/marksheets/download/${filename}`,
};

// Dashboard API
export const dashboardApi = {
  getAdminStats: () => api.get('/dashboard/admin'),
  getTeacherStats: () => api.get('/dashboard/teacher'),
  getStudentStats: () => api.get('/dashboard/student'),
  getParentStats: () => api.get('/dashboard/parent'),
};

// Teacher-specific API
export const teacherApi = {
  getClasses: () => api.get('/teacher/classes'),
  getClassStudents: (classId) => api.get(`/teacher/students/${classId}`),
};

// Homework API
export const homeworkApi = {
  getAll: (params) => api.get('/homework', { params }),
  getById: (id) => api.get(`/homework/${id}`),
  create: (data) => api.post('/homework', data),
  update: (id, data) => api.put(`/homework/${id}`, data),
  delete: (id) => api.delete(`/homework/${id}`),
  getShareText: (id) => api.get(`/homework/${id}/share`),
  publish: (id) => api.post(`/homework/${id}/publish`),
};

// Events API
export const eventsApi = {
  getAll: (params) => api.get('/events', { params }),
  getUpcoming: (limit = 5) => api.get('/events/upcoming', { params: { limit } }),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
};

// School Settings API
export const schoolSettingsApi = {
  get: () => api.get('/school/settings'),
  update: (data) => api.put('/school/settings', data),
};

// Fee Management API
export const feesApi = {
  list: (params) => api.get('/fees', { params }),
  getStudent: (studentId) => api.get(`/fees/student/${studentId}`),
  recordPayment: (data) => api.post('/fees/payment', data),
  recordSplitPayment: (data) => api.post('/fees/payment/split', data),
  downloadReceipt: (paymentId) => `${API_URL}/api/fees/receipt/${paymentId}/pdf`,
  getStructure: (params) => api.get('/fees/structure', { params }),
  setStructure: (data) => api.post('/fees/structure', data),
  setStructureBulk: (data) => api.post('/fees/structure/bulk', data),
  deleteStructureForClass: (classId, academicYear) =>
    api.delete(`/fees/structure/${classId}`, { params: { academic_year: academicYear } }),
  deletePayment: (paymentId) => api.delete(`/fees/payment/${paymentId}`),
  clearAllForSchool: (schoolCode) => api.delete('/fees/school/clear', { params: { confirm: schoolCode } }),
  whatsappReminder: (studentId) => api.get(`/fees/reminder/whatsapp/${studentId}`),
  whatsappRemindersBulk: (params) => api.get('/fees/reminder/whatsapp/bulk/unpaid', { params }),
};

// Report Card API
export const reportCardApi = {
  downloadUrl: (studentId, academicYear = '2026-27') =>
    `${API_URL}/api/reportcard/${studentId}/pdf?academic_year=${encodeURIComponent(academicYear)}`,
};

// Super Admin Invoices API (vendor billing - EduTrack -> Schools)
export const superadminInvoicesApi = {
  list: (params) => api.get('/superadmin/invoices', { params }),
  create: (data) => api.post('/superadmin/invoices', data),
  update: (id, data) => api.put(`/superadmin/invoices/${id}`, data),
  delete: (id) => api.delete(`/superadmin/invoices/${id}`),
  pdfUrl: (id) => `${API_URL}/api/superadmin/invoices/${id}/pdf`,
  exportUrl: (status, schoolId) => {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    if (schoolId) qs.set('school_id', schoolId);
    const q = qs.toString();
    return `${API_URL}/api/superadmin/invoices/export${q ? `?${q}` : ''}`;
  },
};

// Super Admin Schools API
export const superadminSchoolsApi = {
  list: () => api.get('/superadmin/schools'),
};

// Super Admin Vendor Settings API
export const vendorSettingsApi = {
  get: () => api.get('/superadmin/vendor-settings'),
  update: (data) => api.put('/superadmin/vendor-settings', data),
  uploadLogo: (formData) => api.post('/superadmin/vendor-settings/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Super Admin Quotations API
export const quotationsApi = {
  list: () => api.get('/superadmin/quotations'),
  create: (data) => api.post('/superadmin/quotations', data),
  delete: (id) => api.delete(`/superadmin/quotations/${id}`),
  pdfUrl: (id) => `${API_URL}/api/superadmin/quotations/${id}/pdf`,
};

// Examination / Question Paper API
export const examApi = {
  // Question bank
  listQuestions: (params) => api.get('/exam/questions', { params }),
  createQuestion: (data) => api.post('/exam/questions', data),
  updateQuestion: (id, data) => api.put(`/exam/questions/${id}`, data),
  deleteQuestion: (id) => api.delete(`/exam/questions/${id}`),
  // Papers
  listPapers: (params) => api.get('/exam/papers', { params }),
  getPaper: (id) => api.get(`/exam/papers/${id}`),
  createPaper: (data) => api.post('/exam/papers', data),
  updatePaper: (id, data) => api.put(`/exam/papers/${id}`, data),
  submitPaper: (id) => api.post(`/exam/papers/${id}/submit`),
  approvePaper: (id) => api.post(`/exam/papers/${id}/approve`),
  rejectPaper: (id, reason) => api.post(`/exam/papers/${id}/reject`, { reason }),
  lockPaper: (id) => api.post(`/exam/papers/${id}/lock`),
  generateSets: (id, sets = 2) => api.post(`/exam/papers/${id}/generate-sets`, null, { params: { sets } }),
  pdfUrl: (id) => `${API_URL}/api/exam/papers/${id}/pdf`,
  // Audit
  listAudit: (params) => api.get('/exam/audit', { params }),
  // Syllabus (chapters)
  listSyllabus: (params) => api.get('/exam/syllabus', { params }),
  upsertSyllabus: (data) => api.post('/exam/syllabus', data),
  extractSyllabusPdf: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/exam/syllabus/extract-pdf', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // AI
  aiGenerateQuestions: (data) => api.post('/exam/ai/generate-questions', data, { timeout: 120000 }),
  aiGeneratePaper: (data) => api.post('/exam/ai/generate-paper', data, { timeout: 180000 }),
  aiSaveQuestions: (data) => api.post('/exam/ai/save-questions', data),
};

// Bulk student promotion API
export const promotionApi = {
  preview: (academic_year) => api.get('/promotions/preview', { params: { academic_year } }),
  execute: (data) => api.post('/promotions/execute', data),
  undo: (snapshotId) => api.post(`/promotions/${snapshotId}/undo`),
  snapshots: () => api.get('/promotions/snapshots'),
};

// School profile / logo API (Admin)
export const schoolProfileApi = {
  uploadLogo: (formData) => api.post('/schools/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export default api;
